/**
 * Panel registry (PXL). One functional panel per implemented system, opened from
 * the launcher bar / hotkeys by the window-manager Shell. Keep `hotkey` in sync
 * with the Settings keybind reference and the screen-flow doc.
 */

import {
  CharacterPanel,
  EquipmentPanel,
  InventoryPanel,
  ProgressionPanel,
} from './CharacterPanels.js';
import { CraftingPanel, QuestsPanel, TradePanel } from './WorldPanels.js';
import { MarketplacePanel, StorePanel } from './EconomyPanels.js';
import { GuildPanel, MailPanel, SocialPanel } from './SocialPanels.js';
import { EndgamePanel, HousingPanel } from './InstancePanels.js';
import { AccessibilityPanel, DebugPanel, SettingsPanel } from './SystemPanels.js';
import { PlaytestPanel } from './PlaytestPanel.js';

/** Vite replaces the literal `import.meta.env.DEV` at build time, so in production this is a
 *  constant `false` and the dev panels below are tree-shaken out of the bundle. Same-module on
 *  purpose: a call through the devFlags helper was NOT folded (measured 2026-10-24). `?.` keeps
 *  the module loadable under `node --test`, where `import.meta.env` is undefined. */
const DEV_PANELS = import.meta.env?.DEV === true;

export interface PanelDef {
  /** Also the launcher / window-title icon id: `assets/ui/hud-icons/<id>.svg` (HudIcon.tsx,
   *  q-hud-panel-button-icons). No text glyph — the owner read the old "Ch" as an ability. */
  id: string;
  title: string;
  hotkey: string;
  width?: number;
  Component: () => JSX.Element;
}

export const PANELS: PanelDef[] = [
  { id: 'character', title: 'Character', hotkey: 'C', Component: CharacterPanel },
  { id: 'inventory', title: 'Inventory', hotkey: 'I', Component: InventoryPanel },
  { id: 'equipment', title: 'Equipment', hotkey: 'K', Component: EquipmentPanel },
  {
    id: 'progression',
    title: 'Progression',
    hotkey: 'N',
    width: 380,
    Component: ProgressionPanel,
  },
  { id: 'quests', title: 'Quests', hotkey: 'L', Component: QuestsPanel },
  { id: 'crafting', title: 'Crafting', hotkey: 'B', Component: CraftingPanel },
  { id: 'trade', title: 'Trade', hotkey: 'T', Component: TradePanel },
  {
    id: 'marketplace',
    title: 'Marketplace',
    hotkey: 'H',
    width: 420,
    Component: MarketplacePanel,
  },
  {
    id: 'store',
    title: 'Atelier Store',
    hotkey: 'G',
    width: 400,
    Component: StorePanel,
  },
  { id: 'social', title: 'Social', hotkey: 'O', width: 380, Component: SocialPanel },
  { id: 'guild', title: 'Guild', hotkey: 'U', width: 380, Component: GuildPanel },
  { id: 'mail', title: 'Mail', hotkey: 'J', Component: MailPanel },
  {
    id: 'housing',
    title: 'Housing',
    hotkey: 'P',
    width: 380,
    Component: HousingPanel,
  },
  {
    id: 'endgame',
    title: 'Endgame',
    hotkey: 'Y',
    width: 380,
    Component: EndgamePanel,
  },
  {
    id: 'settings',
    title: 'Settings',
    hotkey: 'F1',
    // The Keybinds list is derived from this registry (keybinds.ts), never hand-kept.
    Component: () => <SettingsPanel panels={PANELS} />,
  },
  {
    id: 'accessibility',
    title: 'Accessibility',
    hotkey: 'F2',
    Component: AccessibilityPanel,
  },
  // Developer Tools (the ` panel, q-debug-panel-shipped-to-players, AD-820) and the Playtest
  // control panel (O-026) — DEV BUILDS ONLY. `DEV_PANELS` reads
  // `import.meta.env.DEV`, which Vite replaces with a compile-time literal, so in
  // a production build this spread is statically empty and PlaytestPanel is
  // dead-code-eliminated: the deployed closed playtest ships no dev surface.
  ...(DEV_PANELS
    ? [
        {
          id: 'debug',
          title: 'Developer Tools',
          hotkey: '`',
          width: 420,
          Component: DebugPanel,
        },
        {
          id: 'playtest',
          title: 'Playtest',
          hotkey: 'F9',
          width: 400,
          Component: PlaytestPanel,
        },
      ]
    : []),
];
